/* eslint-disable @typescript-eslint/no-explicit-any */
import { createMemo, createRoot, createSignal } from "solid-js";
import { type SetStoreFunction, createStore, reconcile } from "solid-js/store";

import {
  BindPutInfo,
  GetWithdrawChannelV4,
  UserApplyWithdrawV3,
} from "@/api/clientApi/wallet.ts";
import { message } from "@/components/Base/Message/Message";
import { toast } from "@/components/Base/Toaster/Toaster.tsx";
import { useDialog } from "@/components/Dialogs/useDialog";
import { PutGroupEnum, PutTypeEnum } from "@/enum/wallet.ts";
import { useUser } from "@/hooks/useUser";
import { useWithdraw } from "@/hooks/wallet/useWithdraw.ts";
import { getLangObject, t } from "@/i18n";
import { formatToBindPoint, pointFormatterOrigin } from "@/lib/pointUtils";
import {
  isEmptyObject,
  pureNumber,
  removeParenthesesContent,
  userNameToNFC,
} from "@/lib/utils";
import { cryptoStore } from "@/stores/cryptoStore.ts";
import { KYCStore } from "@/stores/kycStore.ts";
import { sysConfigStore } from "@/stores/sysConfigStore.ts";
import { bindPoint } from "@/stores/userStore";
import { UserKycAuthActionType } from "@/types/compontent/base/baseEnum.ts";
import {
  BindPutInfoReq,
  FiatWithdrawBankCfgV4,
  FiatWithdrawChannelV4,
  GetWithdrawChannelV4Rsp,
  PayIdentify,
  PutTypeCfg,
  UserApplyWithdrawV3Req,
  UserApplyWithdrawV3Rsp,
  UserNamesInfo,
  WithdrawBindInfo,
  WithdrawLimitCfg,
} from "@/types/swagger/userwealth.ts";
import {
  BankAccountTypeCfgMap,
  WithdrawBindInfoMap,
} from "@/types/swaggerex/userwealthex";

const { getBankCardInfo } = useWithdraw();
const { operateDialog } = useDialog();
// 类型定义
export type CurrentPayInfoType = FiatWithdrawChannelV4 & {};

interface ChannelClassWithList {
  id: number;
  sort: number;
  list: FiatWithdrawChannelV4[];
  lang_text?: LangTextSingle;
}

export type LangTextSingle = {
  name: string;
  tip: string;
  desc?: string;
  video_url?: string;
  video_desc?: string;
};

interface DefaultClass {
  class: string;
  list: FiatWithdrawChannelV4[];
  lang_text?: LangTextSingle;
}

type FiatClassMap = Record<
  number | string,
  ChannelClassWithList | DefaultClass
>;

export type WithdrawInfoField =
  | "cpf"
  | "fullName"
  | "bankName"
  | "accountType"
  | "branchNumber"
  | "accountNumber"
  | "point";
export type WithdrawInfoFromType = "input" | "pix" | "select" | "point";
export type FormFieldType = "text" | "number" | "tel";

export interface FromOptions {
  label: string;
  value: string | number;
  type?: number;
}

export interface WithdrawFromInfo {
  name: WithdrawInfoField;
  type: WithdrawInfoFromType;
  showSearch?: boolean;
  inputType: FormFieldType;
  lock?: boolean;
  label?: string;
  error: string;
  value: string;
  maxLength?: number;
  placeholder?: string;
  options?: FromOptions[];
  regex?: string;
  isAvailable?: boolean;
}

export interface BaseItem {
  label: string;
  value: number | string;
  type?: number;
}

export interface TWalletStore {
  crypto_list: []; //加密货币列表
  fait_list: FiatClassMap; //法币列表
  fiat_currency_config: BaseItem[];
  withdrawTable: number; //货币tab 1:法币、0:加密货币
  withdrawTabList: BaseItem[];
  pointType: number; //选中提款货币
  currentPayInfo: FiatWithdrawChannelV4; //选中的提现渠道
  putTypeCfgInfo: PutTypeCfg; //选中渠道的提现类型
  putTypeCfgIndex: number; //选中渠道的配置下标
  currentGroupId: number; //选中的付款渠道id
  withdrawFiatStep: number; //法币提现步骤：1.选择币种 2.填写form
  refreshMoney: boolean; //刷新金额动画
  enterDialogList: FromOptions[]; //提现确认弹窗列表
  identifys: Record<number, PayIdentify>; //证件信息
  bankList: FiatWithdrawBankCfgV4[]; //提现方式列表
  bankAccountTypeCfg: BankAccountTypeCfgMap; //提现方式配置
  selectBankNameList: BaseItem[]; //银行名称下拉列表
  selectBankTypeList: BaseItem[]; //银行类型下拉列表
  namesInfo: UserNamesInfo; //用户名信息
  // bindIdentifys: WithdrawBindInfo; //绑定证件信息
  pixBindBankInfo: UserApplyWithdrawV3Req; //pix合规银行提现信息
  withdrawBindInfo: WithdrawBindInfoMap; //提现绑定信息
  withdrawBankBindList: WithdrawBindInfo[]; //提现银行绑定信息
  chooseBankCard: WithdrawBindInfo; //选中银行卡信息
  isAddBankCardLoading: boolean; //添加银行卡loading
  isWithdrawLoading: boolean; //提现loading
  withdrawLimitCfg: WithdrawLimitCfg; //提现限制配置
}

export interface BankCardStore {
  bindBankCardList: WithdrawBindInfo[]; //绑定银行卡列表
}

export interface FiatFormProps {
  fromStore: Record<string, WithdrawFromInfo>;
  setFromStore: SetStoreFunction<Record<string, WithdrawFromInfo>>;
  type: number; // 1:提现界面 2：添加银行卡界面
  inputClass?: string;
  wrapperClass?: string;
  selectIconBg?: boolean; //筛选框按钮背景色
}

const withdrawTabList: BaseItem[] = [
  {
    value: 0,
    label: "Crypto",
  },
  {
    value: 1,
    label: "Fiat",
  },
];
const initialState: TWalletStore = {
  crypto_list: [],
  fait_list: {},
  fiat_currency_config: [],
  withdrawTable: 1,
  withdrawTabList: withdrawTabList,
  pointType: 5,
  currentPayInfo: {} as FiatWithdrawChannelV4,
  putTypeCfgInfo: {} as PutTypeCfg,
  putTypeCfgIndex: 0,
  currentGroupId: 7,
  withdrawFiatStep: 1,
  refreshMoney: false,
  enterDialogList: [],
  identifys: {},
  bankList: [],
  bankAccountTypeCfg: {},
  selectBankNameList: [],
  selectBankTypeList: [],
  namesInfo: {} as UserNamesInfo,
  chooseBankCard: {} as WithdrawBindInfo,
  // bindIdentifys: {} as WithdrawBindInfo,
  pixBindBankInfo: {} as UserApplyWithdrawV3Req,
  withdrawBindInfo: {} as WithdrawBindInfoMap,
  withdrawBankBindList: [],
  isAddBankCardLoading: false,
  isWithdrawLoading: false,
  withdrawLimitCfg: {} as WithdrawLimitCfg,
};
const initialBankCardState: BankCardStore = {
  bindBankCardList: [],
};
const { updateUserPoint } = useUser();
export const [withdrawStore, setWithdrawStore] = createStore<TWalletStore>(
  JSON.parse(JSON.stringify(initialState)),
);
// 提现表单
export const [faitFromStore, setFaitFromStore] = createStore<
  Record<string, WithdrawFromInfo>
>({});
// 添加银行卡表单
export const [bankFromStore, setBankFromStore] = createStore<
  Record<string, WithdrawFromInfo>
>({});
// 银行卡列表
export const [bankCardStore, setBankCardStore] =
  createStore<BankCardStore>(initialBankCardState);
// 提现loading
export const [withdrawLoading, setWithdrawLoading] =
  createSignal<boolean>(false);
// 初始化store
export const initWithdrawStore = async (isAddCard?: boolean) => {
  setWithdrawStore(reconcile(initialState));
  setFaitFromStore(reconcile({}));
  setBankFromStore(reconcile({}));
  setWithdrawLoading(false);
  await updateUserPoint();
  await getWithdrawInfo(isAddCard);
};
// 初始化银行卡列表
export const initBankCardStore = () => {
  setBankCardStore(reconcile(initialBankCardState));
};
// 提现方式
export const handleWithdrawalMethod = (val: number) => {
  setWithdrawStore("withdrawFiatStep", val);
};
// 获取提现信息
export const getWithdrawInfo = async (isAddCard?: boolean) => {
  const pointType = sysConfigStore.point_type;
  setWithdrawStore("pointType", pointType);
  handleWithdrawalMethod(1);
  await GetWithdrawChannelV4({
    point_type: pointType,
  }).then(async (res: GetWithdrawChannelV4Rsp | undefined) => {
    if (res) {
      setWithdrawFrom(res, isAddCard);
    }
  });
};
// 设置默认选中渠道的提现类型
const setDefaultPutTypeCfgInfo = (index: number) => {
  setWithdrawStore("putTypeCfgIndex", index);
  setWithdrawStore(
    "putTypeCfgInfo",
    withdrawStore.currentPayInfo.bank_list[index]?.put_type_cfg?.[0] ?? {},
  );
};
// 设置提现From
const setWithdrawFrom = (res: GetWithdrawChannelV4Rsp, isAddCard?: boolean) => {
  let fiatChannel: FiatWithdrawChannelV4 = {} as FiatWithdrawChannelV4;
  if (isAddCard) {
    fiatChannel =
      res?.fiat_channels.find(
        (item) => item.group_id == PutGroupEnum.bankCard,
      ) ?? ({} as FiatWithdrawChannelV4);
  } else {
    fiatChannel = res?.fiat_channels?.[0] ?? {};
  }
  setWithdrawStore("currentPayInfo", fiatChannel);
  setWithdrawStore("identifys", res?.identifys ?? {});
  setWithdrawStore("currentGroupId", withdrawStore.currentPayInfo.group_id);
  setWithdrawStore("bankList", withdrawStore.currentPayInfo?.bank_list ?? []);
  setWithdrawStore("bankAccountTypeCfg", res?.bank_account_type_cfg ?? {});
  setWithdrawStore("withdrawBindInfo", res?.bind_info ?? {});
  setWithdrawStore("namesInfo", res?.names_info ?? {});
  setWithdrawStore("withdrawLimitCfg", res?.withdraw_limit_cfg ?? {});
  setWithdrawStore(
    "withdrawBankBindList",
    filterBankList(
      res?.bind_info ?? {},
      isAddCard
        ? PutGroupEnum.bankCard
        : (res?.fiat_channels?.[0]?.group_id ?? 0),
    ),
  );
  // setWithdrawStore(
  //   "bindIdentifys",
  //   filterBankList(res?.bind_info ?? {}, PutGroupEnum.pix)?.[0] ?? {},
  // );

  setWithdrawStore(
    "chooseBankCard",
    withdrawStore.withdrawBankBindList?.[0] ?? {},
  );
  if (withdrawStore.currentPayInfo.group_id == PutGroupEnum.pix) {
    const index =
      withdrawStore.currentPayInfo.bank_list?.findIndex(
        (item) => item.put_type_cfg?.[0]?.put_type == PutTypeEnum.pixCpf,
      ) ?? 0;
    setDefaultPutTypeCfgInfo(index);
    setFaitFrom(withdrawStore.currentPayInfo.group_id);
  } else {
    // setDefaultPutTypeCfgInfo(0);
    setFaitFrom(withdrawStore.currentPayInfo.group_id);
    setPixBankFrom();
  }
  // 暂时不用
  const isFiat: boolean = false;
  if (isFiat) {
    const fiatClass: FiatClassMap = {};
    if (res.channel_class_cfg) {
      Object.values(res.channel_class_cfg).forEach((item) => {
        fiatClass[item.id] = {
          ...item,
          list: [] as FiatWithdrawChannelV4[],
          lang_text: item.lang_text[Object.keys(item.lang_text)[0]],
        };
      });
    }

    res.fiat_channels.forEach((el: any) => {
      if (el.class_ids) {
        for (let i = 0; i < el.class_ids.length; i++) {
          if (fiatClass[el.class_ids[i]]) {
            fiatClass[el.class_ids[i]].list.push(el);
          }
        }
      } else {
        if (fiatClass["default"]) {
          fiatClass["default"].list.push(el);
        } else {
          fiatClass["default"] = {
            class: "存款方式",
            list: [el],
          };
        }
      }
    });
    setWithdrawStore("fait_list", reconcile(fiatClass));
  }
};
// 筛选银行卡列表
const filterBankList = (info: WithdrawBindInfoMap, groupId: PutGroupEnum) => {
  const bankList: WithdrawBindInfo[] = [];
  if (info) {
    for (const key in info) {
      const cardList =
        info[key]?.filter((item) => item.group_id == groupId) || [];
      bankList.push(...cardList);
    }
  }
  return bankList;
};
// 设置pix合归盘from
export const setPixBankFrom = () => {
  const list = withdrawStore.bankList?.map((item: FiatWithdrawBankCfgV4) => {
    const bankText = Object.keys(item.bank_text)[0];
    const text = item.bank_text[bankText].name;
    return {
      label: text,
      value: text,
    };
  });
  setWithdrawStore("selectBankNameList", list);
  setFaitFromStore("bankName", "options", list);

  if (isBoundBank()) {
    const cardInfo = withdrawStore.chooseBankCard;
    onBankNameChange(
      removeParenthesesContent(cardInfo?.entrus_bank_name || ""),
      1,
    );
  }
};
// 银行名称切换
export const onBankNameChange = (value: string, type: number) => {
  const list: BaseItem[] = [];
  withdrawStore.bankList.forEach((item) => {
    const bankText = Object.keys(item.bank_text)[0];
    const text = item.bank_text[bankText].name;
    const typeCfg = withdrawStore.bankAccountTypeCfg;
    if (text == value) {
      item.put_type_cfg.forEach((el) => {
        list.push({
          label: typeCfg[el.account_type || 1].name,
          value: el.put_type,
          type: el.account_type,
        });
      });
    }
  });

  setWithdrawStore("selectBankTypeList", list);
  if (type === 1) {
    setFaitFromStore("accountType", {
      ...faitFromStore.accountType,
      value: isBoundBank() ? String(withdrawStore.chooseBankCard.put_type) : "",
      options: list,
    });
  } else {
    setBankFromStore("accountType", {
      ...bankFromStore.accountType,
      value: "",
      options: list,
    });
  }
};
// 银行类型切换
export const onAccountTypeChange = (value: number, type: number) => {
  // type: 1:法币 2:银行卡
  let branchReg = "";
  let accountReg = "";
  const types = withdrawStore.bankAccountTypeCfg;
  const typeList =
    type === 1
      ? faitFromStore["accountType"].options
      : bankFromStore["accountType"].options;
  typeList?.forEach((item) => {
    if (item.value == value) {
      const type = item.type || 1;
      branchReg = types[type]?.branch_regex_expr ?? "";
      accountReg = types[type]?.regex_expr ?? "";
    }
  });
  if (type === 1) {
    setFaitFromStore("branchNumber", "regex", branchReg);
    setFaitFromStore("accountNumber", "regex", accountReg);
  } else {
    setBankFromStore("branchNumber", "regex", branchReg);
    setBankFromStore("accountNumber", "regex", accountReg);
  }
};
// 账户只读 true:只读 false:可编辑
const isAccountRead = () => {
  return withdrawStore.chooseBankCard?.status == 0 ? true : false;
};
// const isAccountRead = createRoot(() =>
//   createMemo((): boolean => {
//     withdrawStore.currentGroupId == PutGroupEnum.pix
//       ? withdrawStore.withdrawBindInfo[PutTypeEnum.pixCpf]?.[0]
//       : withdrawStore.withdrawBankBindList?.[0];
//     return bindInfo?.status == 0 ? true : false;
//   }),
// );
// 证件只读 true:只读 false:可编辑
const isIdentifyRead = () => {
  if (isAccountRead()) {
    return true;
  }
  if (Object.keys(withdrawStore.identifys).length > 0) {
    const type = withdrawStore.chooseBankCard?.identify_num_type;
    if (!type) return false;
    return withdrawStore.identifys[type]?.dis_edit ?? false;
  }
  return false;
};
// const isIdentifyRead = createRoot(() =>
//   createMemo((): boolean => {
//     if (isAccountRead()) {
//       return true;
//     }
//     if (withdrawStore.identifys?.length) {
//       const bindInfo =
//         withdrawStore.currentGroupId == PutGroupEnum.pix
//           ? withdrawStore.withdrawBindInfo[PutTypeEnum.pixCpf]?.[0]
//           : withdrawStore.withdrawBankBindList?.[0];

//       const identifyNumType = bindInfo?.identify_num_type;
//       const identify = withdrawStore.identifys.find(
//         (item) => item?.identify_type == identifyNumType,
//       );
//       return identify?.dis_edit || false;
//     }
//     return false;
//   }),
// );
// 获取证件信息
export const getIdentify = (identifyType: string, groupId: number) => {
  const identifys = {
    value: "",
    isRead: false,
  };
  const cardInfo = withdrawStore.chooseBankCard;
  const identifyInfo = withdrawStore?.identifys?.[1] ?? {};
  const namesInfo = withdrawStore.namesInfo;
  let cardValue = "";
  let identifyValue = "";
  let nameValue = "";
  if (identifyType == "cpf") {
    cardValue =
      groupId == PutGroupEnum.pix
        ? (cardInfo?.entrus_bank_account ?? "")
        : (cardInfo?.identify_num ?? "");
    identifyValue = identifyInfo?.identify ?? "";
  } else if (identifyType == "name") {
    cardValue = cardInfo?.entrus_bank_user ?? "";
    identifyValue = identifyInfo?.name ?? "";
    nameValue = namesInfo?.full_name ?? "";
  }
  if (cardValue) {
    identifys.value = cardValue;
    // 0.绑定成功, 1.未绑定成功
    identifys.isRead = cardInfo?.status == 0;
    return identifys;
  }
  // 合规银行
  if (groupId == PutGroupEnum.bankCard) {
    if (identifyValue) {
      identifys.value = identifyValue;
      // true:只读 false:可编辑
      identifys.isRead = identifyInfo?.dis_edit;
      return identifys;
    }
  }

  if (identifyType != "cpf" && nameValue) {
    identifys.value = nameValue;
    // true:只读 false:可编辑
    identifys.isRead = namesInfo?.full_dis_edit ?? false;
    return identifys;
  }

  return identifys;
};
// 银行卡是否绑定成功
const isBankBindSuccess = (name: string): boolean => {
  const bankBindInfo = withdrawStore.chooseBankCard;
  if (isEmptyObject(bankBindInfo)) return false;
  if (bankBindInfo[name as keyof WithdrawBindInfo]) {
    return bankBindInfo.status == 0 ? true : false;
  }
  return false;
};
// 获取提现所需信息
export const getWithdrawParams = (params: UserApplyWithdrawV3Req) => {
  // 提现数据
  const currentPayInfo = withdrawStore.currentPayInfo;
  // const putTypeCfgInfo = withdrawStore.putTypeCfgInfo;
  params.channel_id = currentPayInfo.id;
  params.point_type = withdrawStore.pointType;
  // 上次人脸识别id
  params.action_id = "";
  params.point =
    Number(faitFromStore["point"].value) * currentPayInfo.point_ratio;
  handleWithdraw(params);
};
// 获取绑定提现信息
export const getBindPutInfo = (isAddPage: boolean = false) => {
  const bParams: BindPutInfoReq = {} as BindPutInfoReq;
  const wParams: UserApplyWithdrawV3Req = {} as UserApplyWithdrawV3Req;
  const putTypeCfgInfo = withdrawStore.putTypeCfgInfo;
  if (withdrawStore.currentGroupId == PutGroupEnum.pix) {
    // pix提现
    const cpfNum = pureNumber(faitFromStore["cpf"]?.value ?? "");
    bParams.entrus_bank_account = cpfNum;
    wParams.entrus_bank_account = cpfNum;
    bParams.put_type = putTypeCfgInfo.put_type;
    wParams.put_type = putTypeCfgInfo.put_type;
  }
  if (withdrawStore.currentGroupId == PutGroupEnum.bankCard) {
    // pix合规盘
    const chooseCard = withdrawStore.chooseBankCard;
    const isBindBankAll: boolean =
      !!chooseCard.entrus_bank_user &&
      !!chooseCard.bank_branch &&
      !!chooseCard.identify_num &&
      !!chooseCard.identify_num_type;
    if (isBoundBank() && !isAddPage && isBindBankAll) {
      // 已绑定银行
      wParams.entrus_bank_account = faitFromStore["accountNumber"].value;
      wParams.put_type = Number(faitFromStore["accountType"].value);
      getWithdrawParams(wParams);
    } else {
      setWithdrawStore("isAddBankCardLoading", true);
      onBindBankCard(isAddPage)
        .then(() => {
          if (isAddPage) {
            getBankCardInfo()
              .then(() => {
                operateDialog("BankCardAdd");
              })
              .finally(() => {
                setWithdrawStore("isAddBankCardLoading", false);
              });
          } else {
            const params = { ...wParams, ...withdrawStore.pixBindBankInfo };
            getWithdrawParams(params);
          }
        })
        .finally(() => {
          setWithdrawStore("isAddBankCardLoading", false);
        });
    }
    t("tips.bank_added");
    return;
  }
  handleBindPutInfo(bParams, wParams);
};
// 绑定银行卡
export const onBindBankCard = async (isAddPage: boolean) => {
  const params: BindPutInfoReq = {
    id: "",
    entrus_bank_account: isAddPage
      ? bankFromStore["accountNumber"].value
      : faitFromStore["accountNumber"].value,
    entrus_bank_user: isAddPage
      ? bankFromStore["fullName"].value
      : faitFromStore["fullName"].value,
    entrus_bank_name: isAddPage
      ? bankFromStore["bankName"].value
      : faitFromStore["bankName"].value,
    bank_branch: isAddPage
      ? bankFromStore["branchNumber"].value
      : faitFromStore["branchNumber"].value,
    identify_num: isAddPage
      ? pureNumber(bankFromStore["cpf"]?.value ?? "")
      : pureNumber(faitFromStore["cpf"]?.value ?? ""),
    put_type: isAddPage
      ? Number(bankFromStore["accountType"].value)
      : Number(faitFromStore["accountType"].value),
    identify_num_type: 1,
  };
  setWithdrawStore("pixBindBankInfo", params);
  await BindPutInfo(params).then(() => {
    if (isAddPage) {
      toast.success(t("tips.bank_added"));
    }
  });
};
// 提现-绑定提现信息
export const handleBindPutInfo = (
  params: BindPutInfoReq,
  wParams: UserApplyWithdrawV3Req,
) => {
  let needBind: boolean = false;
  // 绑定数据
  // const params: BindPutInfoReq = {} as BindPutInfoReq;
  // const putTypeCfgInfo = withdrawStore.putTypeCfgInfo;
  // const bindInfo = withdrawStore.putTypeCfgInfo.bind_info_list?.[0];
  const bindInfo = withdrawStore.withdrawBindInfo[params.put_type]?.[0];
  const bindRuleCfg = withdrawStore.putTypeCfgInfo.bind_rule_cfg;
  // 提现方式
  // params.put_type = putTypeCfgInfo.put_type;
  // 首次绑定
  if (bindInfo?.id) {
    params.id = bindInfo.id;
  } else {
    params.id = "";
  }
  // 未绑定成功
  if (bindInfo?.status) {
    needBind = true;
  }
  // 真实全名
  if (bindRuleCfg?.need_real_name) {
    params.entrus_bank_user = userNameToNFC(faitFromStore["fullName"].value);
    if (!bindInfo?.entrus_bank_user) {
      needBind = true;
    }
  }
  // 支持的证件类型列表
  if (bindRuleCfg?.identify_types?.length) {
    // 暂时为cpf
    params.identify_num_type = 1;
    if (!isIdentifyRead()) {
      needBind = true;
    }
  }
  // 是否需要证件号码
  if (bindRuleCfg?.need_identify_num) {
    // 暂时为cpf
    params.identify_num = pureNumber(faitFromStore["cpf"]?.value ?? "");
    if (!isIdentifyRead()) {
      needBind = true;
    }
  }
  // 是否需要银行账户开户支行
  if (bindRuleCfg?.need_bank_branch) {
    // 暂时为cpf
    params.bank_branch = faitFromStore["branchNumber"].value;
    if (!isIdentifyRead()) {
      needBind = true;
    }
  }
  // 是否需要银行账户开户支行
  if (bindRuleCfg?.need_bank_branch) {
    // 暂时为cpf
    params.bank_branch = faitFromStore["branchNumber"].value;
    if (!isIdentifyRead()) {
      needBind = true;
    }
  }
  // 是否需要邮箱
  // if (bindRuleCfg?.need_email) {
  //   params.email = faitFromStore["email"].value;
  //   needBind = true;
  // }
  // 是否需要手机号
  // if (bindRuleCfg?.need_phone) {
  //   params.phone = faitFromStore["phone"].value;
  //   needBind = true;
  // }

  if (needBind) {
    BindPutInfo(params).then(() => {
      getWithdrawParams(wParams);
    });
  } else {
    getWithdrawParams(wParams);
  }
};

// 提现确认
export const handleWithdraw = async (params: UserApplyWithdrawV3Req) => {
  if (withdrawLoading()) return;
  setWithdrawLoading(true);
  await KYCStore.getUserKycAuthByAction(
    UserKycAuthActionType.withdraw,
    () =>
      UserApplyWithdrawV3(params)
        .then((res: UserApplyWithdrawV3Rsp | undefined) => {
          if (res?.user_auth) return res;
          message.show(t("withdraw.submitted_tip"));
          const { operateDialog } = useDialog();
          operateDialog("WithdrawEnter");
        })
        .finally(() => {
          setWithdrawLoading(false);
        }) as any,
  );
  // await UserApplyWithdrawV3(params)
  //   .then((res: UserApplyWithdrawV3Rsp | undefined) => {
  //     console.log("res", res);
  //     message.show(t("withdraw.submitted_tip"));
  //     const { operateDialog } = useDialog();
  //     operateDialog("WithdrawEnter");
  //   })
  //   .finally(() => {
  //     setWithdrawLoading(false);
  //   });
};

// 获取提示渠道文本
export const getLangText = createRoot(() =>
  createMemo(() => {
    return getLangObject(withdrawStore.currentPayInfo?.lang_text) || {};
  }),
);
// 获取加密货币名称
export const getCryptoName = createRoot(() =>
  createMemo(() => {
    return cryptoStore.crypto_maps.get(sysConfigStore.point_type)?.name;
  }),
);

// 现金是否需要打码
export const isWithdrawNoCode = createRoot(() =>
  createMemo((): boolean => {
    return (bindPoint()?.code ?? 0) <= 0;
  }),
);
//提现可用金额
export const withdrawAvailable = createRoot(() =>
  createMemo(() => {
    const point = bindPoint()?.code ? 0 : (bindPoint()?.point ?? 0);
    return pointFormatterOrigin(point, withdrawStore.pointType);
  }),
);
// 有打码量时 无时返回现金余额
export const withdrawAvailableCode = createRoot(() =>
  createMemo(() => {
    const point = bindPoint()?.point ?? 0;
    return formatToBindPoint({
      value: pointFormatterOrigin(point, withdrawStore.pointType),
      pointType: withdrawStore.pointType,
      isConverted: true,
      precision: 2,
    });
  }),
);

export const pointFormatterOriginCode = (point: number) => {
  return pointFormatterOrigin(point, withdrawStore.pointType);
};

// 提现可用金额精度值
export const withdrawAvailableValue = (showSymbol: boolean = false) => {
  const point = formatToBindPoint({
    value: withdrawAvailable(),
    pointType: withdrawStore.pointType,
    isConverted: true,
    precision: 2,
    showSymbol: showSymbol,
  });
  return point;
};
// 总彩金
export const amountBonus = createRoot(() =>
  createMemo(() => {
    const code = formatToBindPoint({
      value: pointFormatterOrigin(
        bindPoint()?.bonus ?? 0,
        withdrawStore.pointType,
      ),
      pointType: withdrawStore.pointType,
      isConverted: true,
      precision: 2,
      showSymbol: false,
    });
    return code;
  }),
);
// 需要的彩金总打码量
export const amountBonusCode = createRoot(() =>
  createMemo(() => {
    const code = formatToBindPoint({
      value: pointFormatterOrigin(
        bindPoint()?.bonusCode ?? 0,
        withdrawStore.pointType,
      ),
      pointType: withdrawStore.pointType,
      isConverted: true,
      precision: 2,
    });
    return code;
  }),
);

//提现锁定金额
export const withdrawClockFund = createRoot(() =>
  createMemo((): number => {
    const point = bindPoint()?.code ? (bindPoint()?.point ?? 0) : 0;
    const bonus = (bindPoint()?.bonus ?? 0) + point;
    const newBonus = pointFormatterOrigin(bonus, withdrawStore.pointType);
    return newBonus;
  }),
);
// 设置提现from
export const setFaitFrom = (groupId: number) => {
  let faitFrom: Record<string, WithdrawFromInfo> = {};
  if (groupId == PutGroupEnum.pix) {
    const bindRuleCfg = withdrawStore.putTypeCfgInfo.bind_rule_cfg;
    const nameInfo = getIdentify("name", PutGroupEnum.pix);
    const cpfInfo = getIdentify("cpf", PutGroupEnum.pix);
    faitFrom = {
      cpf: {
        name: "cpf",
        type: "pix",
        inputType: "text",
        lock: cpfInfo.isRead,
        value: cpfInfo.value,
        label: t("withdraw.pix_key_type"),
        error: "",
        regex: "",
        isAvailable: true,
      },
      fullName: {
        name: "fullName",
        type: "input",
        inputType: "text",
        lock: nameInfo.isRead,
        value: nameInfo.value.replace(/#/g, " "),
        label: t("withdraw.account_holder_name"),
        error: "",
        regex: "",
        isAvailable: bindRuleCfg?.need_real_name,
      },
      point: {
        name: "point",
        type: "point",
        inputType: "tel",
        lock: false,
        value: "",
        label: "",
        error: "",
        placeholder: t("withdraw.amount_input", {
          val: getCryptoName() ?? "",
        }),
        regex: "/[^0-9]/g",
        isAvailable: true,
      },
    };
  } else {
    const cardInfo = withdrawStore.chooseBankCard;
    const nameInfo = getIdentify("name", PutGroupEnum.bankCard);
    const cpfInfo = getIdentify("cpf", PutGroupEnum.bankCard);
    faitFrom = {
      cpf: {
        name: "cpf",
        type: "pix",
        inputType: "text",
        lock: cpfInfo.isRead,
        value: cpfInfo.value,
        label: t("withdraw.cpf"),
        placeholder: t("withdraw.cpf"),
        error: "",
        regex: "",
        isAvailable: true,
      },
      fullName: {
        name: "fullName",
        type: "input",
        inputType: "text",
        lock: nameInfo.isRead,
        value: nameInfo.value.replace(/#/g, " "),
        label: t("withdraw.account_holder_name"),
        error: "",
        placeholder: t("withdraw.account_holder_name"),
        regex: "",
        isAvailable: true,
      },
      bankName: {
        name: "bankName",
        type: "select",
        showSearch: true,
        inputType: "text",
        lock: isBankBindSuccess("entrus_bank_name"),
        value: removeParenthesesContent(cardInfo?.entrus_bank_name ?? ""),
        label: t("withdraw.bank_name"),
        error: "",
        options: [],
        regex: "",
        isAvailable: true,
      },
      accountType: {
        name: "accountType",
        type: "select",
        inputType: "text",
        lock: isBankBindSuccess("put_type"),
        value: cardInfo?.put_type ? String(cardInfo?.put_type) : "",
        label: t("withdraw.account_type"),
        error: "",
        options: [],
        regex: "",
        isAvailable: true,
      },
      branchNumber: {
        name: "branchNumber",
        type: "input",
        inputType: "number",
        lock: isBankBindSuccess("bank_branch"),
        value: cardInfo?.bank_branch || "",
        label: t("withdraw.branch_number"),
        error: "",
        placeholder: t("withdraw.branch_number"),
        regex: "",
        isAvailable: true,
      },
      accountNumber: {
        name: "accountNumber",
        type: "input",
        inputType: "number",
        lock: isBankBindSuccess("entrus_bank_account"),
        value: cardInfo?.entrus_bank_account || "",
        label: t("withdraw.account_number"),
        error: "",
        placeholder: t("withdraw.account_number"),
        regex: "",
        isAvailable: true,
      },
      point: {
        name: "point",
        type: "point",
        inputType: "tel",
        lock: false,
        value: "",
        label: "",
        error: "",
        placeholder: t("withdraw.amount_input", {
          val: getCryptoName() ?? "",
        }),
        regex: "/[^0-9]/g",
        isAvailable: true,
      },
    };
  }

  setFaitFromStore(
    Object.fromEntries(
      Object.entries(faitFrom).filter(([key, value]) => value.isAvailable),
    ),
  );
};
// 初始化添加银行卡弹窗
export const initBankCardAdd = () => {
  // const cardInfo = withdrawStore.chooseBankCard;
  // const identifys = withdrawStore.bindIdentifys;
  // const identify =
  //   cardInfo?.identify_num ?? identifys?.entrus_bank_account ?? "";
  // const name = cardInfo?.entrus_bank_user ?? identifys?.entrus_bank_user ?? "";
  const cpfInfo = getIdentify("cpf", PutGroupEnum.bankCard);
  const nameInfo = getIdentify("name", PutGroupEnum.bankCard);
  const bankNameList = withdrawStore.selectBankNameList;
  const bankFrom: Record<string, WithdrawFromInfo> = {
    cpf: {
      name: "cpf",
      type: "pix",
      inputType: "text",
      lock: cpfInfo.isRead,
      value: cpfInfo.value,
      label: t("withdraw.cpf"),
      placeholder: t("withdraw.cpf"),
      error: "",
      regex: "",
    },
    fullName: {
      name: "fullName",
      type: "input",
      inputType: "text",
      lock: nameInfo.isRead,
      value: nameInfo.value.replace(/#/g, " "),
      label: t("withdraw.account_holder_name"),
      placeholder: t("withdraw.account_holder_name"),
      error: "",
      regex: "",
    },
    bankName: {
      name: "bankName",
      type: "select",
      showSearch: true,
      inputType: "text",
      lock: false,
      value: "",
      label: t("withdraw.bank_name"),
      error: "",
      options: bankNameList,
      regex: "",
    },
    accountType: {
      name: "accountType",
      type: "select",
      inputType: "text",
      lock: false,
      value: "",
      label: t("withdraw.account_type"),
      error: "",
      options: [],
      regex: "",
    },
    branchNumber: {
      name: "branchNumber",
      type: "input",
      inputType: "number",
      lock: false,
      value: "",
      label: t("withdraw.branch_number"),
      error: "",
      placeholder: t("withdraw.branch_number"),
      regex: "",
    },
    accountNumber: {
      name: "accountNumber",
      type: "input",
      inputType: "number",
      lock: false,
      value: "",
      label: t("withdraw.account_number"),
      error: "",
      placeholder: t("withdraw.account_number"),
      regex: "",
    },
  };
  setBankFromStore(bankFrom);
};
// 设置提现确认弹窗列表
export const setEnterDialogList = () => {
  let enterList: FromOptions[] = [];
  if (withdrawStore.currentGroupId == PutGroupEnum.pix) {
    enterList = [
      {
        label: t("withdraw.cpf"),
        value: faitFromStore["cpf"].value,
      },
      {
        label: t("withdraw.account_holder_name"),
        value: faitFromStore["fullName"].value.replace(/#/g, " "),
      },
    ];
  } else {
    enterList = [
      {
        label: t("withdraw.cpf"),
        value: faitFromStore["cpf"].value,
      },
      {
        label: t("withdraw.account_holder_name"),
        value: faitFromStore["fullName"].value.replace(/#/g, " "),
      },
      {
        label: t("withdraw.bank_name"),
        value: faitFromStore["bankName"].value,
      },
      {
        label: t("withdraw.account_number"),
        value: faitFromStore["accountNumber"].value,
      },
    ];
  }
  setWithdrawStore("enterDialogList", enterList);
};
// 是否未绑定过银行卡
export const isBoundBank = () => {
  return withdrawStore.withdrawBankBindList?.length ? true : false;
};
